import { Close } from "@mui/icons-material";
import styles from "./settings-dialog.module.css";
import { CircleFlag } from "react-circle-flags";
import { MdDone } from "react-icons/md";
import { currencies, getCurrency } from "../../../lib/currency-data/currency";
import { useCallback, useEffect, useRef, useState } from "react";
import { useUserAuthDataContext } from "@/global-context/user-auth-context";
import { useServerAction } from "zsa-react";
import {
  updateCurrencyAction,
  updateUnitSystemAction,
} from "./actions/actions";
import { toast } from "react-toastify";
import { useLocale, useTranslations } from "next-intl";
import {
  getLocaleTarget,
  localesData,
  WORLD_LABEL_BY_LOCALE,
} from "@/lib/locale-data/locale";
import { usePathname } from "@/i18n/navigation";
import { useSearchParams } from "next/navigation";
import { handleCreateHandoff, handleLocaleClick } from "./actions/handlers";
import { useScrollLock } from "@/hooks/use-scroll-lock";
import { useDialogRouting } from "@/hooks/use-dialog-routing";
import { useDomainContext } from "@/global-context/domain-context";
import { translateExperiencePath } from "@/lib/experience/experience-paths";

export default function SettingsDialog({
  isSettingsOpen,
  closeDialog,
}: {
  isSettingsOpen: boolean;
  closeDialog: () => void;
}) {
  const currentLocale = useLocale();
  const { userData, setUserData } = useUserAuthDataContext();
  const userCurrency = getCurrency(userData.currency);
  const currentDomain = useDomainContext();
  const userLocale = getLocaleTarget({
    domain: currentDomain,
    locale: currentLocale,
  });
  const toggleRef = useRef<HTMLDivElement | null>(null);
  const [isCurrencyActive, setIsCurrencyActive] = useState(true);
  const pathName = usePathname();
  const searchParams = useSearchParams();
  const t = useTranslations("SettingsDialog");

  const { execute: updateCurrencyExecute } =
    useServerAction(updateCurrencyAction);

  const { execute: updateUnitSystemExecute } = useServerAction(
    updateUnitSystemAction,
  );

  useDialogRouting("SettingsDialog", isSettingsOpen, closeDialog);

  useScrollLock(isSettingsOpen);

  return (
    <>
      {isSettingsOpen && (
        <div className={styles.dialogWrapper} onClick={closeDialog}>
          <div
            ref={toggleRef}
            className={styles.refWrapper}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={styles.contentWrapper}>
              <button
                className={styles.closeButton}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  closeDialog();
                }}
              >
                <Close />
              </button>
              <div className={styles.unitSystemWrapper}>
                <h2 className={styles.selectionTitle}>{t("unitSystem")}</h2>
                <span className={styles.description}>
                  {t("unitSystemPreference")}
                </span>
                <div className={styles.unitButtonsWrapper}>
                  <button
                    className={`${styles.selectButton} ${
                      userData.unit_system === "Metric" && styles.isSelected
                    }`}
                    onClick={async () => {
                      const currentState = userData.unit_system;
                      if (currentState === "Metric") {
                        return;
                      }
                      setUserData({ ...userData, unit_system: "Metric" });
                      const [data, err] = await updateUnitSystemExecute({
                        unit_system: "Metric",
                      });
                      if ((data !== null && data !== undefined) || err) {
                        setUserData({ ...userData, unit_system: currentState });
                        toast(
                          data?.message === "Failed to fetch" || err
                            ? "Unable to update your data."
                            : data?.message,
                          {
                            type: "error",
                          },
                        );
                      }
                    }}
                  >
                    {t("metric")}
                  </button>
                  <button
                    className={`${styles.selectButton} ${
                      userData.unit_system === "Imperial" && styles.isSelected
                    }`}
                    onClick={async () => {
                      const currentState = userData.unit_system;
                      if (currentState === "Imperial") {
                        return;
                      }
                      setUserData({ ...userData, unit_system: "Imperial" });
                      const [data, err] = await updateUnitSystemExecute({
                        unit_system: "Imperial",
                      });
                      if ((data !== null && data !== undefined) || err) {
                        setUserData({ ...userData, unit_system: currentState });
                        toast(
                          data?.message === "Failed to fetch" || err
                            ? "Unable to update your data."
                            : data?.message,
                          {
                            type: "error",
                          },
                        );
                      }
                    }}
                  >
                    {t("imperial")}
                  </button>
                </div>
              </div>
              <div className={styles.currencyAndLanguageWrapper}>
                <h2 className={styles.selectionTitle}>
                  {t("currencyLanguage")}
                </h2>
                <div className={styles.selectionWrapper}>
                  <button
                    className={`${styles.selectionButton} ${
                      isCurrencyActive && styles.isSelected
                    }`}
                    onClick={(e) => {
                      setIsCurrencyActive(true);
                    }}
                  >
                    {t("currency")}
                  </button>
                  <button
                    className={`${styles.selectionButton} ${
                      !isCurrencyActive && styles.isSelected
                    }`}
                    onClick={(e) => {
                      setIsCurrencyActive(false);
                    }}
                  >
                    {t("language")}
                  </button>
                </div>
                {isCurrencyActive ? (
                  <span className={styles.description}>
                    {t("currencyPreference")}
                  </span>
                ) : (
                  <span className={styles.description}>
                    {t("languagePreference")}
                  </span>
                )}
                {isCurrencyActive ? (
                  <ul className={styles.currencyList}>
                    {/* <li
                      className={`${styles.listItem} ${styles.isSelected}`}
                      key={userCurrency.Code}
                    >
                      <div className={styles.itemWrapper}>
                        <div className={styles.textWrapper}>
                          <span className={styles.itemTitle}>
                            {userCurrency.FullName}
                          </span>
                          <span className={styles.itemAbbr}>
                            {userCurrency.Name}
                          </span>
                        </div>
                      </div>
                      <MdDone className={styles.selectedIcon} />
                    </li> */}
                    {currencies.map((currency) => {
                      if (currency.Name === userCurrency.Name) {
                        return (
                          <li
                            className={`${styles.listItem} ${styles.isSelected}`}
                            key={userCurrency.Code}
                          >
                            <div className={styles.itemWrapper}>
                              <div className={styles.textWrapper}>
                                <span className={styles.itemTitle}>
                                  {userCurrency.FullName}
                                </span>
                                <span className={styles.itemAbbr}>
                                  {userCurrency.Name}
                                </span>
                              </div>
                            </div>
                            <MdDone className={styles.selectedIcon} />
                          </li>
                        );
                      }
                      return (
                        <li key={currency.Code}>
                          <button
                            className={styles.listItem}
                            onClick={async () => {
                              const currentState = userData.currency;
                              if (currentState === currency.Name) {
                                return;
                              }
                              setUserData({
                                ...userData,
                                currency: currency.Name,
                              });
                              const [data, err] = await updateCurrencyExecute({
                                currency: currency.Name,
                              });
                              if (
                                (data !== null && data !== undefined) ||
                                err
                              ) {
                                setUserData({
                                  ...userData,
                                  currency: currentState,
                                });
                                toast(
                                  data?.message === "Failed to fetch" || err
                                    ? "Unable to update your data."
                                    : data?.message,
                                  {
                                    type: "error",
                                  },
                                );
                              }
                            }}
                          >
                            <div className={styles.itemWrapper}>
                              <div className={styles.textWrapper}>
                                <span className={styles.itemTitle}>
                                  {currency.FullName}
                                </span>
                                <span className={styles.itemAbbr}>
                                  {currency.Name}
                                </span>
                              </div>
                            </div>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <ul className={styles.languageList}>
                    <li
                      className={`${styles.listItem} ${styles.countryItem} ${styles.isSelected}`}
                      key={`${userLocale.domain}:${userLocale.locale}:${userLocale.region}`}
                    >
                      <div className={styles.itemWrapper}>
                        {userLocale.region === "GLOBAL" ? (
                          <div className={styles.countryIcon}>
                            <img
                              className={styles.globeImage}
                              src="/globe.png"
                              alt="Globe image"
                            />
                          </div>
                        ) : (
                          <CircleFlag
                            countryCode={userLocale.region.toLowerCase()}
                            className={styles.countryIcon}
                          />
                        )}
                        <div className={styles.textWrapper}>
                          <span className={styles.itemTitle}>
                            {userLocale.name}
                          </span>
                          <span className={`${styles.country}`}>
                            {getCountryName(userLocale.region, currentLocale)}
                          </span>
                        </div>
                      </div>
                      <MdDone className={styles.selectedIcon} />
                    </li>
                    {localesData.map((locale) => {
                      const isCurrentLocaleTarget =
                        locale.domain === userLocale.domain &&
                        locale.locale === userLocale.locale &&
                        locale.region === userLocale.region;

                      if (isCurrentLocaleTarget) {
                        return null;
                      }
                      return (
                        <li
                          key={`${locale.domain}:${locale.locale}:${locale.region}`}
                        >
                          <button
                            className={`${styles.listItem} ${styles.countryItem}`}
                            onClick={async () => {
                              await handleLocaleClick(locale.locale.trim());

                              const queryString = searchParams.toString();

                              const newPath = translateExperiencePath({
                                pathname: pathName,
                                fromLocale: currentLocale,
                                toLocale: locale.locale,
                                fromDomain: currentDomain,
                                toDomain: locale.domain,
                                searchParams: queryString,
                              });

                              const baseUrl = getBaseUrl(locale.domain);
                              let finalUrl = `${baseUrl}${newPath}`;

                              const isCrossDomain =
                                locale.domain !== userLocale.domain;

                              if (isCrossDomain && userData.sessionId) {
                                const handOffToken =
                                  await handleCreateHandoff();

                                if (handOffToken) {
                                  const separator = finalUrl.includes("?")
                                    ? "&"
                                    : "?";
                                  finalUrl += `${separator}handoff=${handOffToken}`;
                                }
                              }

                              window.location.href = finalUrl;
                            }}
                          >
                            <div className={styles.itemWrapper}>
                              {locale.region === "GLOBAL" ? (
                                <div className={styles.countryIcon}>
                                  <img
                                    className={styles.globeImage}
                                    src="/globe.png"
                                  />
                                </div>
                              ) : (
                                <CircleFlag
                                  countryCode={locale.region.toLowerCase()}
                                  className={styles.countryIcon}
                                />
                              )}

                              <div className={styles.textWrapper}>
                                <span className={styles.itemTitle}>
                                  {locale.name}
                                </span>
                                <span className={`${styles.country}`}>
                                  {getCountryName(locale.region, currentLocale)}
                                </span>
                              </div>
                            </div>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function getBaseUrl(domain: string) {
  if (process.env.NODE_ENV === "development") {
    return `http://${domain}:3000`;
  }

  return `https://${domain}`;
}

export function getCountryName(region: string, locale: string) {
  if (region === "GLOBAL") {
    return WORLD_LABEL_BY_LOCALE[locale] ?? "World";
  }

  return new Intl.DisplayNames([locale], { type: "region" }).of(region);
}
